import { useMemo, useState } from "react";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  BANNER_STATE,
  getBannerState,
} from "pages/main/BannerManager/bannerState";
import { toFileKey } from "shared/api/awsS3";
import {
  deleteBanner,
  fetchBanners,
  updateBanner,
} from "shared/api/BannerApi";

// 데이터 로딩 전(undefined)에 매 렌더마다 새 []를 만들지 않도록 고정 참조를 사용한다.
// (새 [] → useMemo 재계산 → activeBanners 불안정 → SearchPanel effect 무한 루프)
const EMPTY_BANNERS = [];

export function useBannerManagerData() {
  const queryClient = useQueryClient();
  const [deletionInProgress, setDeletionInProgress] = useState(false);
  const itemsPerPageposting = 3;
  const itemsPerPageend = 5;

  const { data: bannerData = EMPTY_BANNERS } = useQuery({
    queryKey: ["banners"],
    queryFn: fetchBanners,
  });

  const deleteBannerMutation = useMutation({
    mutationFn: deleteBanner,
    onSuccess: (deletedBannerId) => {
      queryClient.setQueryData(["banners"], (previous = []) =>
        previous.filter((banner) => banner.id !== deletedBannerId),
      );
    },
  });

  const updateBannerMutation = useMutation({
    mutationFn: updateBanner,
    onSuccess: (_, variables) => {
      queryClient.setQueryData(["banners"], (previous = []) =>
        previous.map((banner) =>
          banner.id === variables.bannerId
            ? { ...banner, ...variables.data }
            : banner,
        ),
      );
    },
  });

  // hide/start/end + 현재시각으로 게시중 / 미게시 / 게시종료 3분할.
  // bannerData가 바뀔 때만 새 배열을 만든다. 매 렌더마다 새 참조가 생기면
  // SearchPanel의 필터 useMemo/useEffect가 끝없이 재실행되어 무한 루프가 난다.
  const { activeBanners, unpublishedBanners, endedBanners } = useMemo(() => {
    const now = new Date();
    const active = [];
    const unpublished = [];
    const ended = [];
    bannerData.forEach((banner) => {
      const state = getBannerState(banner, now);
      if (state === BANNER_STATE.PUBLISHED) active.push(banner);
      else if (state === BANNER_STATE.ENDED) ended.push(banner);
      else unpublished.push(banner);
    });
    return {
      activeBanners: active,
      unpublishedBanners: unpublished,
      endedBanners: ended,
    };
  }, [bannerData]);

  const updateBannerHideValue = async (bannerId, hideValue) => {
    const bannerToUpdate = bannerData.find((banner) => banner.id === bannerId);
    if (!bannerToUpdate) return;

    // PATCH는 webImageKey/mobileImageKey(S3 key)를 기대한다. 목록 객체는 전체 URL만
    // 들고 있으므로 toFileKey로 변환해 보낸다. (안 보내면 서버가 이미지를 비워버림)
    await updateBannerMutation.mutateAsync({
      bannerId,
      data: {
        title: bannerToUpdate.title,
        hide: hideValue,
        start: bannerToUpdate.start,
        end: bannerToUpdate.end,
        webImageKey: toFileKey(bannerToUpdate.webImage),
        mobileImageKey: toFileKey(bannerToUpdate.mobileImage),
      },
    });
  };

  // 선택 배너들의 hide를 명시적으로 hideValue로 설정한다.
  //  - 게시중 → 미게시 처리: hideValue=true
  //  - 미게시 → 게시 처리:   hideValue=false
  const setHideValues = async (selectedRows, hideValue) => {
    const targetIds = selectedRows.map((selected) => selected.id);
    const updatedBannerData = bannerData.map((banner) =>
      targetIds.includes(banner.id) ? { ...banner, hide: hideValue } : banner,
    );

    queryClient.setQueryData(["banners"], updatedBannerData);
    await Promise.all(
      targetIds.map((bannerId) => updateBannerHideValue(bannerId, hideValue)),
    );
  };

  const deleteSelectedEndedBanners = async (selectedRows) => {
    setDeletionInProgress(true);
    try {
      await Promise.all(
        selectedRows.map((row) => deleteBannerMutation.mutateAsync(row.id)),
      );
    } finally {
      setDeletionInProgress(false);
    }
  };

  return {
    activeBanners,
    bannerData,
    deletionInProgress,
    deleteSelectedEndedBanners,
    endedBanners,
    itemsPerPageend,
    itemsPerPageposting,
    setHideValues,
    unpublishedBanners,
  };
}
